import * as React from 'react'
import { useEffect, useState } from 'react'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Button,
  Heading,
  Image,
  List,
  Text,
  toast,
  UpgradeDialog,
} from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
  Product,
} from '@buffer-mono/tracking-plan'

import {
  useCanStartTrial,
  useCurrentOrganization,
  useOrganizationLimits,
  useSubscription,
} from '~publish/contexts/AccountContext'
import { MODALS, useOpenGlobalModal } from '~publish/hooks/useOpenGlobalModal'
import { useStartTrial } from '~publish/hooks/useStartTrial'
import { SEGMENT_NAMES } from '~publish/legacy/constants'

import styles from './IdeasLimitReachedModal.module.css'

interface Props {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onClose: () => void
}

export const IdeasLimitReachedModal = React.forwardRef<HTMLDivElement, Props>(
  (
    { open = false, onOpenChange, onClose }: Props,
    ref: React.ForwardedRef<HTMLDivElement>,
  ): JSX.Element => {
    const { t } = useTranslation()
    const canStartTrial = useCanStartTrial()
    const subscription = useSubscription()

    const modalType = React.useMemo(() => {
      const planId = subscription?.plan?.id

      if (planId && planId !== 'free') {
        return 'paid'
      }
      return canStartTrial ? 'trial' : 'upgrade'
    }, [subscription, canStartTrial])

    const currentOrganization = useCurrentOrganization()
    const organizationId = currentOrganization?.id
    const { ideas: ideasLimit } = useOrganizationLimits() ?? {}

    const startTrial = useStartTrial()
    const openGlobalModal = useOpenGlobalModal()
    const [isStartingTrial, setIsStartingTrial] = useState(false)

    const handleStartTrial = async (): Promise<void> => {
      if (!organizationId) {
        return
      }
      setIsStartingTrial(true)
      try {
        await startTrial({
          organizationId,
          attribution: {
            cta: `publish-${SEGMENT_NAMES.IDEAS_LIMIT_TRIAL}`,
            upgradePathName: 'ideasLimit-trial',
          },
        })
      } catch (error) {
        toast.error(error instanceof Error ? error.message : String(error))
      } finally {
        setIsStartingTrial(false)
      }
    }

    useEffect(() => {
      if (modalType !== 'paid') {
        const cta =
          modalType === 'trial'
            ? SEGMENT_NAMES.IDEAS_LIMIT_TRIAL
            : SEGMENT_NAMES.IDEAS_LIMIT_UPGRADE
        const eventData = {
          product: Product.Publish,
          upgradePathName: `ideasLimit-${modalType}`,
          organizationId: organizationId || '',
          clientName: Client.PublishWeb,
          cta,
        }

        BufferTracker.upgradePathViewed(eventData)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- We want to run this only once on mount
    }, [])

    const paidContent = (): JSX.Element => {
      return (
        <>
          <Heading size="large">
            {t('content.modals.ideasLimitReached.paid.title', {
              limit: ideasLimit,
            })}
          </Heading>
          <Text color="subtle">
            {t('content.modals.ideasLimitReached.paid.description')}
          </Text>
        </>
      )
    }

    const freeContent = (contentType: string): JSX.Element => {
      return (
        <>
          <Heading size="large">
            {t(`content.modals.ideasLimitReached.free.title`, {
              limit: ideasLimit,
            })}
          </Heading>
          <Text color="subtle">
            {t(`content.modals.ideasLimitReached.free.description`)}
          </Text>
          <List>
            <List.Item color="primary">
              {t(
                `content.modals.ideasLimitReached.free.${contentType}.listItem1`,
              )}
            </List.Item>
            <List.Item color="primary">
              {t(
                `content.modals.ideasLimitReached.free.${contentType}.listItem2`,
              )}
            </List.Item>
            <List.Item color="primary">
              {t(
                `content.modals.ideasLimitReached.free.${contentType}.listItem3`,
              )}
            </List.Item>
          </List>
          {contentType === 'trial' && (
            <Button
              variant="primary"
              onClick={handleStartTrial}
              loading={isStartingTrial}
            >
              {t('content.modals.ideasLimitReached.free.trial.button')}
            </Button>
          )}
          {contentType === 'upgrade' && (
            <Button
              variant="primary"
              onClick={(): void => {
                onClose()
                openGlobalModal(MODALS.planSelector, {
                  cta: SEGMENT_NAMES.IDEAS_LIMIT_UPGRADE,
                  upgradePathName: 'ideasLimit-upgrade',
                })
              }}
            >
              {t('content.modals.ideasLimitReached.free.upgrade.button')}
            </Button>
          )}
          {contentType === 'trial' && (
            <Text color="subtle" weight="medium" className={styles.footer}>
              {t('content.modals.ideasLimitReached.free.trial.footer')}
            </Text>
          )}
        </>
      )
    }

    const getModalContent = (contentType: string): JSX.Element => {
      switch (contentType) {
        case 'paid':
          return paidContent()
        case 'trial':
        case 'upgrade':
          return freeContent(contentType)
        default:
          return paidContent()
      }
    }

    const internalOnOpenChange = React.useCallback(
      (isOpen: boolean): void => {
        if (onOpenChange) {
          onOpenChange(isOpen)
        }
        if (!isOpen) {
          onClose()
        }
      },
      [onClose, onOpenChange],
    )

    return (
      <UpgradeDialog open={open} onOpenChange={internalOnOpenChange}>
        <UpgradeDialog.Content ref={ref}>
          <UpgradeDialog.TextContent>
            {getModalContent(modalType)}
          </UpgradeDialog.TextContent>
          <UpgradeDialog.Image>
            <Image
              src="https://buffer-publish.s3.amazonaws.com/images/ideas-upgrade_2x.png"
              alt="Consumption limits reached"
              placeholder="https://buffer-publish.s3.amazonaws.com/images/ideas-upgrade.png"
            />
          </UpgradeDialog.Image>
        </UpgradeDialog.Content>
      </UpgradeDialog>
    )
  },
)

IdeasLimitReachedModal.displayName = 'IdeasLimitReachedModal'
